/**
 * The dated roadmap, drawn: the workbench timeline renderer's date scale
 * (mju-ai-workbench shared/diagrams/render/timeline.ts, `dateTimeline`) as
 * React SVG on the console's tokens instead of an SVG string through the
 * Diagrammer's pen. The same marks in the same places: a row per item with
 * its status, id and name at the left; bars on a month and quarter axis —
 * finished work solid in its lane's tone, work under way solid to today and
 * planned (or forecast, hatched) after it, a target alone a diamond — the
 * critical path outlined, a late target's slip hatched to where the pace
 * says, what waits on what as arrows, Today as a line, and what has no date
 * listed at the end.
 *
 * Colour is never the only signal: every bar's end says in words what it
 * means, each item's status is a shape, and every mark has a tooltip.
 */
import './gantt.css';

import { roadmapTargetWords } from '@aioe/shared/roadmap/model';
import { type KeyboardEvent, type ReactNode, useId, useMemo } from 'react';

import { dayOf, dayWords, ganttAxis, ganttSpan, type GanttSpan, ganttTicks, monthWords } from './ganttTimeline.ts';
import type { ItemStatus, TimelineDiagram, TimelineItem } from './ganttSpec.ts';

const GUTTER = 300;
const CHART_X = GUTTER + 18;
const LANE_HEAD_H = 30;
const BAR_H = 14;
const ROW_H = 28;
const CHAR_PX = 6.7;

/** A name cut to fit `px` at the label size, with an ellipsis. */
function fit(text: string, px: number): string {
  const room = Math.floor(px / CHAR_PX);
  if (text.length <= room) return text;
  return `${text.slice(0, Math.max(1, room - 1)).trimEnd()}…`;
}

function Diamond({ cx, cy, r, className }: { cx: number; cy: number; r: number; className: string }) {
  return <polygon className={className} points={`${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`} />;
}

/** The status as a shape: a ticked disc for done, a half disc under way, a paused ring deferred, a ring not started. */
function StatusGlyph({ status, cx, cy }: { status: ItemStatus | undefined; cx: number; cy: number }) {
  const r = 5.5;
  if (status === 'done')
    return (
      <g className="gantt-glyph" data-status="done">
        <circle cx={cx} cy={cy} r={r} className="gantt-glyph__fill" />
        <polyline points={`${cx - 2.6},${cy + 0.2} ${cx - 0.6},${cy + 2.2} ${cx + 2.8},${cy - 2}`} className="gantt-glyph__tick" />
      </g>
    );
  if (status === 'in-progress')
    return (
      <g className="gantt-glyph" data-status="in-progress">
        <circle cx={cx} cy={cy} r={r} className="gantt-glyph__ring gantt-glyph__ring--accent" />
        <path d={`M${cx} ${cy - r} A${r} ${r} 0 0 1 ${cx} ${cy + r} Z`} className="gantt-glyph__fill" />
      </g>
    );
  if (status === 'deferred')
    return (
      <g className="gantt-glyph" data-status="deferred">
        <circle cx={cx} cy={cy} r={r} className="gantt-glyph__ring" />
        <rect x={cx - 2.4} y={cy - 2.6} width={1.6} height={5.2} className="gantt-glyph__pause" />
        <rect x={cx + 0.8} y={cy - 2.6} width={1.6} height={5.2} className="gantt-glyph__pause" />
      </g>
    );
  return <circle cx={cx} cy={cy} r={r} className="gantt-glyph__ring" data-status="not-started" />;
}

interface Row {
  item: TimelineItem;
  span: GanttSpan;
  y: number;
  tone: string;
}

const STATUS_WORDS: Record<ItemStatus, string> = { 'not-started': 'not started', 'in-progress': 'in progress', done: 'done', deferred: 'deferred', 'wont-do': "won't do" };

export function GanttChart({ spec, onOpen }: { spec: TimelineDiagram; onOpen?: (id: string) => void }) {
  const scope = `gantt-${useId().replace(/:/g, '')}`;
  const drawing = useMemo(() => layout(spec), [spec]);
  if (!drawing) return null;
  const { axis, ticks, headerH, lanes, rows, undated, height, width, now, critical } = drawing;
  const xOf = (d: number) => CHART_X + (d - axis.lo) * axis.pxPerDay;
  const centre = new Map(rows.map((r) => [r.item.id, { x0: xOf(r.span.from), x1: r.span.bar ? Math.max(xOf(r.span.to + 1), xOf(r.span.from) + 6) : xOf(r.span.from), y: r.y + ROW_H / 2 }]));
  const open = (id: string) => onOpen?.(id);
  const onKey = (id: string) => (e: KeyboardEvent<SVGGElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open(id);
    }
  };

  const arrows: ReactNode[] = [];
  for (const r of rows) {
    const to = centre.get(r.item.id)!;
    for (const dep of r.item.dependsOn ?? []) {
      const from = centre.get(dep);
      if (!from) continue;
      const onPath = critical.has(r.item.id) && critical.has(dep);
      const cls = onPath ? 'gantt-arrow gantt-arrow--critical' : 'gantt-arrow';
      const sx = from.x1 + 2;
      const tx = to.x0 - (r.span.bar ? 3 : 10);
      const dy = to.y > from.y ? 1 : -1;
      if (tx - sx >= 14) {
        arrows.push(
          <g key={`${dep}>${r.item.id}`} className={cls}>
            <polyline points={`${sx},${from.y} ${sx + 6},${from.y} ${sx + 6},${to.y} ${tx},${to.y}`} />
            <path d={`M${tx} ${to.y} L${tx - 6} ${to.y - 3.6} L${tx - 6} ${to.y + 3.6} Z`} className="gantt-arrow__head" />
          </g>,
        );
      } else {
        // The work started before what it waits on ends: a drop from that end onto the bar — it cannot finish first.
        const x = Math.min(Math.max(from.x1, to.x0 + 4), Math.max(to.x0 + 4, to.x1 - 4));
        const ty = to.y - dy * (BAR_H / 2 + 1);
        arrows.push(
          <g key={`${dep}>${r.item.id}`} className={cls}>
            <polyline points={`${from.x1},${from.y + dy * (BAR_H / 2 + 1)} ${x},${from.y + dy * (BAR_H / 2 + 1)} ${x},${ty - dy * 5}`} />
            <path d={`M${x} ${ty} L${x - 3.6} ${ty - dy * 6} L${x + 3.6} ${ty - dy * 6} Z`} className="gantt-arrow__head" />
          </g>,
        );
      }
    }
  }

  return (
    <svg className={`gantt ${scope}`} width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="group" aria-label={`${spec.meta.title}. ${spec.meta.summary}`} data-gantt>
      <defs>
        {[...new Set(rows.map((r) => r.tone))].map((t) => (
          <pattern key={t} id={`${scope}-hatch-${t}`} width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="7" height="7" style={{ fill: `color-mix(in oklab, var(--tone-${t}) 16%, var(--surface))` }} />
            <line x1="0" y1="0" x2="0" y2="7" strokeWidth="2.6" style={{ stroke: `color-mix(in oklab, var(--tone-${t}) 70%, transparent)` }} />
          </pattern>
        ))}
        <pattern id={`${scope}-hatch-late`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="6" height="6" style={{ fill: 'var(--surface)' }} />
          <line x1="0" y1="0" x2="0" y2="6" strokeWidth="2" style={{ stroke: 'var(--danger)' }} />
        </pattern>
      </defs>

      {/* Lane bands and zebra rows, under everything. */}
      {lanes.map((l) => (
        <g key={l.id} style={{ ['--lane' as string]: `var(--tone-${l.tone})` }}>
          <rect x={0} y={l.y} width={width} height={LANE_HEAD_H} className="gantt-lane__band" />
          <rect x={10} y={l.y + 8} width={3.2} height={LANE_HEAD_H - 16} rx={1.6} className="gantt-lane__mark" />
          <text x={22} y={l.y + LANE_HEAD_H / 2 + 4.5} className="gantt-lane__label">
            {fit(l.label, GUTTER - 90)}
          </text>
          <text x={GUTTER - 6} y={l.y + LANE_HEAD_H / 2 + 4} className="gantt-muted gantt-small" textAnchor="end">
            {`${l.count} item${l.count === 1 ? '' : 's'}`}
          </text>
        </g>
      ))}
      {rows.map((r, i) => (i % 2 === 1 ? <rect key={`z${r.item.id}`} x={0} y={r.y} width={width} height={ROW_H} className="gantt-zebra" /> : null))}

      {/* The header: quarters, then months; the grid under the marks. */}
      {ticks.quarters.map((q) => (
        <g key={`q${q.day}`}>
          <text x={xOf(q.day) + 6} y={axis.months ? 18 : 22} className="gantt-quarter">
            {q.label}
          </text>
          <line x1={xOf(q.day)} y1={4} x2={xOf(q.day)} y2={height} className="gantt-rule" />
        </g>
      ))}
      {ticks.months.map((m) => (
        <g key={`m${m.day}`}>
          <text x={xOf(m.day) + 5} y={40} className="gantt-muted gantt-small">
            {m.label}
          </text>
          {!ticks.quarters.some((q) => q.day === m.day) ? <line x1={xOf(m.day)} y1={28} x2={xOf(m.day)} y2={height} className="gantt-grid" /> : null}
        </g>
      ))}
      <line x1={0} y1={headerH - 0.5} x2={width} y2={headerH - 0.5} className="gantt-rule" />
      <line x1={GUTTER} y1={headerH} x2={GUTTER} y2={height} className="gantt-rule" />

      {arrows}

      {rows.map((r) => {
        const { item, span: s, tone } = r;
        const onPath = critical.has(item.id);
        const cy = r.y + ROW_H / 2;
        const idW = item.id.length * 6.6;
        const titleX = 22 + 14 + idW + 8 + (onPath ? 13 : 0);
        const x0 = xOf(s.from);
        const x1 = s.bar ? Math.max(xOf(s.to + 1), x0 + 6) : x0;
        const by = cy - BAR_H / 2;
        const nowX = now !== undefined ? xOf(now + 1) : undefined;
        const split = item.status === 'in-progress' && nowX !== undefined && nowX > x0 + 6 && nowX < x1 - 2 ? nowX : undefined;
        const slipDay = item.forecastEnd && item.status !== 'done' ? dayOf(item.forecastEnd) : undefined;
        const slipX = slipDay !== undefined ? xOf(slipDay + 1) : undefined;
        const late = slipX !== undefined && slipX > x1 + 2;
        const after = late ? slipX! : x1;
        const endWords = item.forecast ? `Forecast · ${monthWords(s.to)}` : item.target && item.status !== 'done' ? `Target ${roadmapTargetWords(item.target)}${late ? ` · forecast ${monthWords(slipDay!)}` : ''}` : item.status === 'done' ? `Finished ${dayWords(s.to)}` : '';
        const tip = [
          `${item.id} ${item.title}`,
          item.status ? STATUS_WORDS[item.status] : undefined,
          s.bar ? `${dayWords(s.from)} to ${dayWords(s.to)}` : dayWords(s.to),
          item.forecast ? 'a forecast, not a promise' : item.target ? `target ${roadmapTargetWords(item.target)}${item.forecastEnd && item.status !== 'done' ? `, but the recent pace says ${monthWords(dayOf(item.forecastEnd) ?? 0)}` : ''}` : undefined,
          item.dependsOn?.length ? `waits on ${item.dependsOn.join(', ')}` : undefined,
          onPath ? 'on the critical path' : undefined,
        ]
          .filter(Boolean)
          .join(' — ');
        const fillFuture = item.forecast ? `url(#${scope}-hatch-${tone})` : undefined;
        return (
          <g
            key={item.id}
            className="gantt-item"
            data-id={item.id}
            data-status={item.status}
            data-critical={onPath || undefined}
            data-forecast={item.forecast || undefined}
            style={{ ['--lane' as string]: `var(--tone-${tone})` }}
            {...(onOpen ? { role: 'button', tabIndex: 0, 'aria-label': tip, onClick: () => open(item.id), onKeyDown: onKey(item.id) } : { 'aria-label': tip })}
          >
            <title>{tip}</title>
            <StatusGlyph status={item.status} cx={26} cy={cy} />
            <text x={36} y={cy + 4} className="gantt-id">
              {item.id}
            </text>
            {onPath ? <Diamond cx={36 + idW + 10} cy={cy} r={4.2} className="gantt-critical-fill" /> : null}
            <text x={titleX} y={cy + 4} className="gantt-title" data-done={item.status === 'done' || undefined}>
              {fit(item.title, GUTTER - titleX - 8)}
            </text>
            {late ? (
              <rect
                x={s.bar ? x1 - BAR_H / 2 : x1 + 8}
                y={by + 2}
                width={slipX! - (s.bar ? x1 - BAR_H / 2 : x1 + 8)}
                height={BAR_H - 4}
                rx={(BAR_H - 4) / 2}
                className="gantt-slip"
                style={{ fill: `url(#${scope}-hatch-late)` }}
              />
            ) : null}
            {s.bar ? (
              item.status === 'done' ? (
                <rect x={x0} y={by} width={x1 - x0} height={BAR_H} rx={BAR_H / 2} className="gantt-bar gantt-bar--solid" />
              ) : split !== undefined ? (
                <>
                  <rect x={x0} y={by} width={x1 - x0} height={BAR_H} rx={BAR_H / 2} className={item.forecast ? 'gantt-bar gantt-bar--forecast' : 'gantt-bar gantt-bar--planned'} style={fillFuture ? { fill: fillFuture } : undefined} />
                  <rect x={x0} y={by} width={split - x0} height={BAR_H} rx={BAR_H / 2} className="gantt-bar gantt-bar--solid gantt-bar--bare" />
                </>
              ) : item.forecast ? (
                <rect x={x0} y={by} width={x1 - x0} height={BAR_H} rx={BAR_H / 2} className="gantt-bar gantt-bar--forecast" style={{ fill: fillFuture }} />
              ) : (
                <rect x={x0} y={by} width={x1 - x0} height={BAR_H} rx={BAR_H / 2} className={item.status === 'in-progress' ? 'gantt-bar gantt-bar--solid' : 'gantt-bar gantt-bar--planned'} />
              )
            ) : (
              <Diamond cx={x0} cy={cy} r={8} className={onPath ? 'gantt-critical-fill' : 'gantt-target'} />
            )}
            {endWords ? (
              <text x={after + (s.bar || late ? 8 : 13)} y={cy + 4} className={late ? 'gantt-end gantt-end--late' : 'gantt-end'}>
                {endWords}
              </text>
            ) : null}
          </g>
        );
      })}

      {undated.items.length ? (
        <g data-gantt-undated>
          <line x1={0} y1={undated.y - 0.5} x2={width} y2={undated.y - 0.5} className="gantt-rule" />
          <text x={22} y={undated.y + 22} className="gantt-heading">
            No date yet
          </text>
          <text x={22} y={undated.y + 40} className="gantt-muted gantt-small">
            {`${undated.items.length} item${undated.items.length === 1 ? '' : 's'} with no target, no forecast and no work recorded`}
          </text>
          {undated.items.map((e) => (
            <g key={e.item.id} className="gantt-item" data-id={e.item.id} style={{ ['--lane' as string]: `var(--tone-${e.tone})` }} {...(onOpen ? { role: 'button', tabIndex: 0, 'aria-label': `${e.item.id} ${e.item.title}`, onClick: () => open(e.item.id), onKeyDown: onKey(e.item.id) } : {})}>
              <title>{`${e.item.id} ${e.item.title}`}</title>
              <rect x={e.x} y={e.y + 4} width={3} height={12} rx={1.5} className="gantt-lane__mark" />
              <text x={e.x + 10} y={e.y + 14.5} className="gantt-id">
                {e.item.id}
              </text>
              <text x={e.x + 10 + e.item.id.length * 6.6 + 7} y={e.y + 14.5} className="gantt-small">
                {fit(e.item.title, UNDATED_COL - 30 - e.item.id.length * 6.6)}
              </text>
            </g>
          ))}
        </g>
      ) : null}

      {now !== undefined && now >= axis.lo && now < axis.hi ? (
        <g className="gantt-today" data-gantt-today>
          <line x1={xOf(now + 0.5)} y1={headerH} x2={xOf(now + 0.5)} y2={height} />
          <rect x={xOf(now + 0.5) - 20} y={headerH + (LANE_HEAD_H - 16) / 2} width={40} height={16} rx={8} />
          <text x={xOf(now + 0.5)} y={headerH + (LANE_HEAD_H - 16) / 2 + 11.5} textAnchor="middle">
            Today
          </text>
        </g>
      ) : null}
    </svg>
  );
}

const UNDATED_COL = 280;

/** Where everything goes: `dateTimeline`'s layout, with one line per name. */
function layout(spec: TimelineDiagram) {
  const spans = new Map(spec.items.map((i) => [i.id, ganttSpan(i)]));
  const dated = [...spans.values()].filter((s): s is GanttSpan => !!s);
  if (!dated.length) return undefined;
  const critical = new Set(spec.criticalPath ?? []);
  const now = spec.now ? dayOf(spec.now) : undefined;
  // A late target's slip reaches past it: the axis runs to where the pace says.
  const slips = spec.items.map((i) => (i.forecastEnd ? dayOf(i.forecastEnd) : undefined)).filter((d): d is number => d !== undefined);
  const axis = ganttAxis([...dated, ...slips.map((d) => ({ from: d, to: d, bar: false }))], now, false);
  const ticks = ganttTicks(axis);
  const headerH = axis.months ? 50 : 34;
  const xOf = (d: number) => CHART_X + (d - axis.lo) * axis.pxPerDay;
  const lanes: Array<{ id: string; label: string; tone: string; y: number; count: number }> = [];
  const rows: Row[] = [];
  const undatedItems: Array<{ item: TimelineItem; tone: string }> = [];
  let tail = 0;
  let y = headerH;
  for (const lane of spec.lanes) {
    const tone = lane.tone ?? 'slate';
    const items = spec.items.filter((i) => i.lane === lane.id);
    const withDates = items.filter((i) => spans.get(i.id)).sort((a, b) => spans.get(a.id)!.from - spans.get(b.id)!.from || spans.get(a.id)!.to - spans.get(b.id)!.to || a.id.localeCompare(b.id));
    undatedItems.push(...items.filter((i) => !spans.get(i.id) && !i.more).map((item) => ({ item, tone })));
    if (!withDates.length) continue;
    lanes.push({ id: lane.id, label: lane.label, tone, y, count: withDates.length });
    y += LANE_HEAD_H;
    for (const item of withDates) {
      const span = spans.get(item.id)!;
      rows.push({ item, span, y, tone });
      const x1 = span.bar ? Math.max(xOf(span.to + 1), xOf(span.from) + 6) : xOf(span.from);
      const slip = item.forecastEnd ? dayOf(item.forecastEnd) : undefined;
      const end = Math.max(x1, slip !== undefined ? xOf(slip + 1) : 0);
      tail = Math.max(tail, end + 13 + 30 * CHAR_PX * 0.9);
      y += ROW_H;
    }
  }
  const width = Math.ceil(Math.max(xOf(axis.hi) + 16, tail + 24, GUTTER + 420));
  const placed: Array<{ item: TimelineItem; tone: string; x: number; y: number }> = [];
  const undatedY = y + 14;
  if (undatedItems.length) {
    const cols = Math.max(1, Math.floor((width - 22) / UNDATED_COL));
    const rowsN = Math.ceil(undatedItems.length / cols);
    undatedItems.forEach((e, i) => placed.push({ ...e, x: 22 + Math.floor(i / rowsN) * UNDATED_COL, y: undatedY + 54 + (i % rowsN) * 21 }));
    y = undatedY + 54 + rowsN * 21 + 8;
  }
  return { axis, ticks, headerH, lanes, rows, undated: { y: undatedY, items: placed }, height: Math.ceil(y), width, now, critical };
}
